import type { SubscriptionPlan } from '@/api/subscription'

export type PricingView = 'independants' | 'equipes' | 'enterprise'
export type FeaturedKind = 'popular' | 'volume'
export type BillingPeriod = 'monthly' | 'yearly'

export interface PlanDescriptor {
  key: SubscriptionPlan
  /** Vue marketing qui doit afficher ce plan. */
  view: PricingView
  /** Prix mensuel CAD (null = Enterprise sur devis). */
  monthlyCad: number | null
  /** Prix annuel /mois CAD (-20%, arrondi vers le bas). */
  yearlyCad: number | null
  /** Économie totale CAD sur l'année. */
  yearlySavings: number
  /** Minutes IA incluses par utilisateur (null = custom Enterprise). */
  aiMinutes: number | null
  /** Facturation par utilisateur ou forfaitaire. */
  perUser: boolean
  featured?: FeaturedKind
  isEnterprise: boolean
  /** Plan dont ce plan hérite (pour "Tout dans X, plus :"). */
  inheritsFrom?: SubscriptionPlan
}

/** Grille tarifaire (sept. 2026) : trois plans, l'IA à part (réceptionniste). */
export const SUBSCRIPTION_PLANS: PlanDescriptor[] = [
  {
    key: 'solo',
    view: 'independants',
    monthlyCad: 24,
    yearlyCad: 19,
    yearlySavings: 60,
    aiMinutes: 30,
    perUser: true,
    isEnterprise: false,
  },
  {
    key: 'team',
    view: 'independants',
    monthlyCad: 49,
    yearlyCad: 39,
    yearlySavings: 120,
    aiMinutes: 30,
    perUser: true,
    featured: 'popular',
    inheritsFrom: 'solo',
    isEnterprise: false,
  },
  {
    key: 'business',
    view: 'equipes',
    monthlyCad: 89,
    yearlyCad: 69,
    yearlySavings: 240,
    aiMinutes: 30,
    perUser: true,
    inheritsFrom: 'team',
    isEnterprise: false,
  },
]

export const CAD_TO_USD = 0.73

export function plansForView(view: PricingView): PlanDescriptor[] {
  return SUBSCRIPTION_PLANS.filter((p) => p.view === view)
}

export function getDisplayPriceCad(plan: PlanDescriptor, billing: BillingPeriod): number | null {
  if (plan.monthlyCad == null) return null
  return billing === 'yearly' ? plan.yearlyCad : plan.monthlyCad
}

/**
 * Retourne le montant numérique formaté (sans symbole devise).
 * Le symbole "$" / "$ USD" est rendu séparément dans le composant carte.
 */
export function formatPrice(amountCad: number, currency: 'cad' | 'usd'): string {
  if (currency === 'usd') {
    return `${Math.round(amountCad * CAD_TO_USD)}`
  }
  return `${amountCad}`
}

/**
 * Features condensées (4 par plan) pour les cards de la BillingView Mode 2
 * "Changer de plan". La liste complète (6-8 features) reste sur la page
 * publique /pricing.
 *
 * Quand on touche cette liste, vérifier la cohérence avec FEATURES_FR du
 * composant PricingPlanGrid (landing publique) — split assumé tant qu'on
 * n'a pas extrait dans un mapping unique.
 */
export const FEATURES_SHORT_FR: Partial<Record<SubscriptionPlan, string[]>> = {
  solo: [
    'Numéro local par utilisateur',
    'Appels et textos illimités CA/US',
    "Agents IA et flux d'appels",
    'Rendez-vous, transcriptions, résumés',
  ],
  team: [
    'Tout du forfait Solo',
    'Numéros partagés et groupes',
    'Horaires par utilisateur',
    "CRM d'équipe : HubSpot, Pipedrive, Zoho, Slack",
  ],
  business: [
    'Tout du forfait Team',
    "Appels sortants par l'agent IA",
    "Power dialer et campagnes d'appels",
    "Analytics d'équipe",
  ],
}

export const FEATURES_SHORT_EN: Partial<Record<SubscriptionPlan, string[]>> = {
  solo: [
    'Local number per user',
    'Unlimited calls and texts CA/US',
    'AI agents and call flows',
    'Appointments, transcripts, summaries',
  ],
  team: [
    'Everything in Solo',
    'Shared numbers and groups',
    'Hours per user',
    'Team CRM: HubSpot, Pipedrive, Zoho, Slack',
  ],
  business: [
    'Everything in Team',
    'Outbound calls by the AI agent',
    'Power dialer and call campaigns',
    'Team analytics',
  ],
}

/** Helper : est-ce que ce slug correspond à un plan moderne (vs legacy) ? */
const MODERN_SLUGS: ReadonlyArray<string> = [
  'solo',
  'team',
  'business',
  'premium',
  'enterprise',
]
/** Les plans qu'on propose encore (Premium et Enterprise ne sont plus vendus). */
export function plansProposes(): PlanDescriptor[] {
  return SUBSCRIPTION_PLANS
}
export function isModernPlanSlug(slug: string): slug is SubscriptionPlan {
  return MODERN_SLUGS.includes(slug)
}
